import type { CategoryDto } from "@/api/categories";

export type CascadingCategory = Pick<CategoryDto, "id" | "parentId" | "name" | "path" | "projectId">;

export type CategorySelectLevel = {
  parent: CascadingCategory | null;
  options: CascadingCategory[];
  value: string | null;
};

/** A required category must be a known terminal node. */
export function isSelectableCategory(categories: readonly CascadingCategory[], value: string | null): boolean {
  return value !== null && categories.some((category) => category.id === value) && !categories.some((category) => category.parentId === value);
}

/** Resolve labels from the directory rather than trusting a stale display path. */
export function categorySelectionPath(categories: readonly CascadingCategory[], value: string | null): CascadingCategory[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const path: CascadingCategory[] = [];
  const visited = new Set<string>();
  let current = value ? byId.get(value) : undefined;
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }

  return current ? [] : path;
}

/** Derive columns from a browsing path, independently of the committed category. */
export function categorySelectLevels(categories: readonly CascadingCategory[], value: string | null): CategorySelectLevel[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const children = new Map<string | null, CascadingCategory[]>();
  for (const category of categories) {
    const parentId = category.parentId && byId.has(category.parentId) ? category.parentId : null;
    const siblings = children.get(parentId) ?? [];
    siblings.push(category);
    children.set(parentId, siblings);
  }
  const path = categorySelectionPath(categories, value);
  const roots = children.get(null) ?? [];
  const rootOptions = [...roots.filter((category) => !category.projectId), ...roots.filter((category) => category.projectId)];
  const levels: CategorySelectLevel[] = [{ parent: null, options: rootOptions, value: path[0]?.id ?? null }];
  for (let index = 0; index < path.length; index++) {
    const parent = path[index];
    if (!parent) continue;
    const options = children.get(parent.id) ?? [];
    if (options.length > 0) levels.push({ parent, options, value: path[index + 1]?.id ?? null });
  }
  return levels;
}
